import { BoxProps, Panel } from '@hover/blueprint';
import React, { useReducer } from 'react';
import { animated, config, useSpring } from 'react-spring';
import { useDrag } from 'react-use-gesture';
import styled from 'styled-components';

const DEFAULT_CARD_HEIGHT_PERCENTAGE = 50;
const OPEN_CARD_HEIGHT_PERCENTAGE = 80;

const Container = styled(animated.div)`
  display: flex;
  width: 100%;
  height: 24px;
  justify-content: center;
  position: absolute;
  pointer-events: auto;
`;

const TabContainer = styled(animated.div)`
  position: absolute;
  top: -12px;
  display: flex;
  width: 100%;
  height: 48px;
  justify-content: center;
`;

const Tab = styled(Panel)`
  border-radius: 15px;
  opacity: 0.9;
`;

enum CardPosition {
  OPEN = 'open',
  COLLAPSED = 'collapsed',
  HIDDEN = 'hidden',
}

const reducer = (_: number, action: CardPosition) => {
  switch (action) {
    case CardPosition.OPEN:
      return OPEN_CARD_HEIGHT_PERCENTAGE;
    case CardPosition.COLLAPSED:
      return DEFAULT_CARD_HEIGHT_PERCENTAGE;
    case CardPosition.HIDDEN:
      return 0;
    default:
      return DEFAULT_CARD_HEIGHT_PERCENTAGE;
  }
};

type AnimatedContainerProps = {
  closeMenu?: () => void;
  disabled?: boolean;
} & BoxProps;

const AnimatedContainer = ({
  children,
  closeMenu,
  disabled = false,
}: AnimatedContainerProps) => {
  const [cardHeight, dispatch] = useReducer(
    reducer,
    DEFAULT_CARD_HEIGHT_PERCENTAGE,
  );

  const [{ height }, set] = useSpring(() => ({
    height: DEFAULT_CARD_HEIGHT_PERCENTAGE,
  }));

  const open = (canceled: boolean) => {
    // when cancel is true, it means that the user passed the upwards threshold
    // so we change the spring config to create a nice wobbly effect
    dispatch(CardPosition.OPEN);
    set({
      height: OPEN_CARD_HEIGHT_PERCENTAGE,
      immediate: false,
      config: canceled ? config.wobbly : config.stiff,
    });
  };

  const collapse = () => {
    dispatch(CardPosition.COLLAPSED);
    set({
      height: DEFAULT_CARD_HEIGHT_PERCENTAGE,
      immediate: false,
      config: { ...config.stiff },
    });
  };

  const close = () => {
    if (closeMenu) closeMenu();
    dispatch(CardPosition.HIDDEN);
    set({
      height: 0,
      immediate: false,
      config: { ...config.stiff },
    });
  };

  const bind = useDrag(
    ({ last: lastEvent, vxvy: [, vy], movement, canceled, tap }) => {
      // ignore event if registered as a tap
      if (tap) {
        return;
      }

      // calculate percentage based on window height
      const changeInPercentage =
        ((movement[1] * -1) / window.innerHeight) * 100;
      const newCardHeight = cardHeight + changeInPercentage;
      if (lastEvent) {
        if (newCardHeight < 40) {
          close();
          return;
        }
        if (vy > 0.5 || changeInPercentage < -20) {
          collapse();
        } else {
          open(canceled);
        }
      } else {
        set({
          height: newCardHeight,
          immediate: true,
        });
      }
    },
    {
      initial: () => [0, height.getValue()],
      bounds: { height: OPEN_CARD_HEIGHT_PERCENTAGE },
      rubberband: false,
      axis: 'y',
      useTouch: true,
      enabled: !disabled,
    },
  );

  return (
    <Container
      style={{
        bottom: 0,
        height: height
          .interpolate({ range: [0, 100], output: ['0%', '100%'] })
          .interpolate(h => h),
      }}
    >
      {!disabled && (
        <TabContainer {...bind()}>
          <Tab color="neutral000" height={25} padding={0} width="33vw" />
        </TabContainer>
      )}
      {children}
    </Container>
  );
};

export { AnimatedContainer };
